// «Kapasitet» under Tjenester og priser: hvor mange minutter verkstedet har per dag og uke i hver
// butikk, og når ski tas imot og slipes. Brukes av bookingen på nettsiden og Sjekk kapasitet.
import React, { useState } from 'react';
import { Valgfelt, knappPrimar } from '../../ny-kundesamhandling/Skjema';
import Ikon from '../../ny-kundesamhandling/Ikon';
import { LOKASJON_NAVN, DAG_NAVN, lagreIKatalog, sistEndret, endredeFelt } from './katalogFelles';
import { Avkrysning, Flervalg, Tallfelt, Melding, Endringslogg } from './Velgere';

const DAGER = Object.entries(DAG_NAVN);
const HELE_DAGNAVN = { mandag: 'Mandag', tirsdag: 'Tirsdag', onsdag: 'Onsdag', torsdag: 'Torsdag', fredag: 'Fredag', lordag: 'Lørdag', sondag: 'Søndag' };
const FELT = [
  'aktiv', 'ukebudsjett', 'dagsbudsjett', 'tidligukebudsjett', 'senukebudsjett', 'dagsbudsjettdager',
  'skiDagsbudsjett', 'skiApneDager', 'skiSlipFristDag', 'skiSlipFerdigDagFra', 'skiSlipFerdigDagTil',
];

function Dagvalg({ id, etikett, verdi, onEndre, disabled }) {
  return (
    <Valgfelt id={id} etikett={etikett} value={verdi || ''} disabled={disabled} onChange={(e) => onEndre(e.target.value || null)} className="w-44 max-w-full">
      <option value="">Ikke satt</option>
      {Object.entries(HELE_DAGNAVN).map(([d, navn]) => <option key={d} value={d}>{navn}</option>)}
    </Valgfelt>
  );
}

function Butikkskjema({ kap, kanEndre, onLagret }) {
  const [original, setOriginal] = useState(() => Object.fromEntries(FELT.map((f) => [f, kap[f] ?? null])));
  const [skjema, setSkjema] = useState(original);
  const [status, setStatus] = useState({ type: '', tekst: '' });
  const sett = (felt) => (v) => setSkjema((s) => ({ ...s, [felt]: v }));
  const id = `k-${kap._id}`;

  async function lagre(e) {
    e.preventDefault();
    setStatus({ type: 'lagrer', tekst: '' });
    try {
      await lagreIKatalog('PATCH', `kapasitet/${kap._id}`, endredeFelt(skjema, original));
      setOriginal(skjema);
      setStatus({ type: 'ok', tekst: 'Lagret' });
      onLagret();
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  return (
    <form onSubmit={lagre} aria-labelledby={`${id}-tittel`} className="space-y-4 rounded-2xl border-2 border-slate-200 bg-white p-3 sm:p-3">
      <h2 id={`${id}-tittel`} className="text-lg font-bold text-slate-900">{LOKASJON_NAVN[kap.lokasjonsNavn] || kap.slug}</h2>
      <fieldset disabled={!kanEndre} className="space-y-4">
        <legend className="sr-only">Kapasitet for {LOKASJON_NAVN[kap.lokasjonsNavn] || kap.slug}</legend>
        <Avkrysning id={`${id}-aktiv`} etikett="Tar imot booking" hjelp="Av: butikken vises ikke i bookingen på nettsiden." verdi={skjema.aktiv} onEndre={sett('aktiv')} />

        <section aria-labelledby={`${id}-sykkel`} className="space-y-3">
          <h3 id={`${id}-sykkel`} className="font-bold text-slate-900">Sykkel (minutter)</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tallfelt id={`${id}-uke`} etikett="Per uke" verdi={skjema.ukebudsjett} onEndre={sett('ukebudsjett')} enhet="min" />
            <Tallfelt id={`${id}-dag`} etikett="Per dag" hjelp="Tomt: uke delt på 5" verdi={skjema.dagsbudsjett} onEndre={sett('dagsbudsjett')} enhet="min" />
            <Tallfelt id={`${id}-tidlig`} etikett="Tidlig i uken" hjelp="Man–ons" verdi={skjema.tidligukebudsjett} onEndre={sett('tidligukebudsjett')} enhet="min" />
            <Tallfelt id={`${id}-sen`} etikett="Sent i uken" hjelp="Ons–fre" verdi={skjema.senukebudsjett} onEndre={sett('senukebudsjett')} enhet="min" />
          </div>
          <Flervalg id={`${id}-dagdager`} etikett="Dager med dagservice" valg={DAGER} verdi={skjema.dagsbudsjettdager} onEndre={sett('dagsbudsjettdager')} disabled={!kanEndre} />
        </section>

        <section aria-labelledby={`${id}-ski`} className="space-y-3">
          <h3 id={`${id}-ski`} className="font-bold text-slate-900">Ski og skøyter</h3>
          <Tallfelt id={`${id}-skidag`} etikett="Minutter per dag" verdi={skjema.skiDagsbudsjett} onEndre={sett('skiDagsbudsjett')} enhet="min" />
          <Flervalg id={`${id}-skidager`} etikett="Dager verkstedet jobber med ski" valg={DAGER} verdi={skjema.skiApneDager} onEndre={sett('skiApneDager')} disabled={!kanEndre} />
          <div className="flex flex-wrap gap-3">
            <Dagvalg id={`${id}-frist`} etikett="Frist for slip" verdi={skjema.skiSlipFristDag} onEndre={sett('skiSlipFristDag')} />
            <Dagvalg id={`${id}-fra`} etikett="Slip ferdig fra" verdi={skjema.skiSlipFerdigDagFra} onEndre={sett('skiSlipFerdigDagFra')} />
            <Dagvalg id={`${id}-til`} etikett="Slip ferdig til" verdi={skjema.skiSlipFerdigDagTil} onEndre={sett('skiSlipFerdigDagTil')} />
          </div>
        </section>
      </fieldset>
      {status.type === 'feil' && <Melding type="feil">{status.tekst}</Melding>}
      {status.type === 'ok' && <Melding type="ok">{status.tekst}</Melding>}
      {kanEndre && (
        <button type="submit" disabled={status.type === 'lagrer'} className={knappPrimar}>
          <Ikon navn="hake" className="w-6 h-6" /> {status.type === 'lagrer' ? 'Lagrer …' : 'Lagre'}
        </button>
      )}
      <p className="text-sm text-slate-600">{sistEndret(kap)}</p>
      <Endringslogg endringer={kap.endringer} />
    </form>
  );
}

export default function Kapasitet({ data, kanEndre, hent }) {
  if (!data.kapasiteter.length) return <Melding>Ingen butikker er satt opp.</Melding>;
  return (
    <div className="space-y-3">
      <p className="text-slate-700">
        Minuttene verkstedet har til booket service. Stengte dager og ekstra eller færre minutter en dag eller uke legger du inn under «Stengte dager og endringer».
      </p>
      {data.kapasiteter.map((k) => <Butikkskjema key={k._id} kap={k} kanEndre={kanEndre} onLagret={hent} />)}
    </div>
  );
}
